import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useAxiosInstance } from "../../services/Interceptor";
import { setProfileAction } from "../../store/AthleteActions";
import { setScoreConfig } from "../../store/slices/athleteSlice";
import { useResponsiveHeight } from "../../hooks/useResponsiveHeight";
import { getStateAbbreviation } from "../../utils/stateAbbreviations";
import CircularProgressBar from "../CircularProgressBar";
import AthleteMeasurements from "../athlete-scores/measurements/athlete-measurements";
import CombineResults from "../athlete-scores/combines/athlete-combine-results";
import AthleteCombineButtons from "../athlete-scores/combines/athlete-combine-buttons";
import PositionDrills from "../athlete-scores/position-drills/positionDrills";
import LoadingSpinner from "../LoadingSpinner";

const SCORE_INFO_TITLE = "Score Info";
const SCORE_INFO_DESCRIPTION =
  "All of the scores on this page represent the percentile compared to other athletes who have attended any Trackit Verified Combine. This should not be viewed as a grade, but as a way to see how you compare to other athletes.";

export default function PublicAthleteProfile({ userId }) {
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const profile = useSelector((state) => state.athletes.profile, shallowEqual);
  const loading = useSelector((state) => state.athletes.loading);
  const buttons = useSelector((state) => state.athletes.buttons);
  const [tab, setTab] = useState(1);
  const [showScoreInfoModal, setShowScoreInfoModal] = useState(false);
  const { adjustedHeight, isMobile } = useResponsiveHeight({
    mobileHeight: 280,
    desktopHeight: 480,
  });

  useEffect(() => {
    dispatch(setProfileAction(apiInstance, userId));
    dispatch(
      setScoreConfig({
        showOffers: false,
        showTrackittScore: true,
        showPositionScore: true,
        searchEvent: false,
        eventId: undefined,
        showProfile: true,
        showRank: true,
        showLikes: false,
        sharedProfile: true,
        showCompare: false,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const style = {
    ul: "flex space-x-0 mt-0 border-b border-gray-300",
    li: "text-gray-900 pb-2 px-3 xl:px-4 hover:opacity-70 text-sm xl:text-base cursor-pointer whitespace-nowrap",
    selected:
      "cursor-default border-b-4 border-blue-700 text-sm xl:text-base text-blue-700 pb-2 px-3 xl:px-4 font-medium whitespace-nowrap",
  };

  if (!profile || loading) {
    return (
      <div className="my-10 lg:w-full xl:h-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-white shadow-lg p-4 rounded-lg border-gray-300 relative md:h-auto md:overflow-visible">
      <div
        className={`top-0 bg-white z-20 ${isMobile === false ? "sticky" : ""}`}
      >
        <PublicAthleteHeader profile={profile} />

        <ul className={style.ul}>
          <li
            className={tab === 1 ? style.selected : style.li}
            onClick={() => setTab(1)}
          >
            Combine Results
          </li>
          <li
            className={tab === 2 ? style.selected : style.li}
            onClick={() => setTab(2)}
          >
            Position Drills
          </li>
        </ul>

        {tab === 1 && buttons.combine.tab && (
          <AthleteCombineButtons profile={profile} />
        )}
      </div>

      {tab === 1 && (
        <div
          className="mt-0 px-4 -m-4 pb-12 md:overflow-y-auto"
          style={
            isMobile === false ? { height: `${adjustedHeight}px` } : undefined
          }
        >
          <div className="pt-0 mb-10">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-medium">Measurements</h1>
              <button
                type="button"
                className="flex items-center bg-white border hover:bg-gray-200 border-gray-300 rounded-md text-sm px-3 py-1.5 cursor-pointer transition"
                onClick={() => setShowScoreInfoModal(true)}
              >
                Score Info
                <span className="pl-2">
                  <svg
                    fill="#000000"
                    width="18px"
                    height="18px"
                    viewBox="-2 0 19 19"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M13.55 15.256H1.45a.554.554 0 0 1-.553-.554V3.168a.554.554 0 1 1 1.108 0v10.98h11.544a.554.554 0 0 1 0 1.108zM3.121 13.02V6.888a.476.476 0 0 1 .475-.475h.786a.476.476 0 0 1 .475.475v6.132zm2.785 0V3.507a.476.476 0 0 1 .475-.475h.786a.476.476 0 0 1 .475.475v9.513zm2.785 0V6.888a.476.476 0 0 1 .475-.475h.786a.476.476 0 0 1 .475.475v6.132zm2.786 0v-2.753a.476.476 0 0 1 .475-.475h.785a.476.476 0 0 1 .475.475v2.753z" />
                  </svg>
                </span>
              </button>
            </div>
            <AthleteMeasurements profile={profile} />
            <h1 className="text-xl font-medium mt-6">Athletic Score</h1>
            <CombineResults profile={profile} />
          </div>
        </div>
      )}

      {tab === 2 && (
        <div
          className="mt-0 px-4 -m-4 pb-12 md:overflow-y-auto"
          style={
            isMobile === false
              ? { height: `${adjustedHeight + 80}px` }
              : undefined
          }
        >
          <PositionDrills userId={userId} />
        </div>
      )}

      {showScoreInfoModal && (
        <ScoreInfoModal
          title={SCORE_INFO_TITLE}
          description={SCORE_INFO_DESCRIPTION}
          onClose={() => setShowScoreInfoModal(false)}
        />
      )}
    </div>
  );
}

function PublicAthleteHeader({ profile }) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="-m-4 grid grid-cols-3 gap-3 bg-white p-4 pb-6 shadow-md md:m-0 md:grid-cols-6 md:bg-transparent md:p-0 md:pb-0 md:shadow-none">
      <div className="col-span-3 md:col-span-2">
        <div className="flex items-start justify-start gap-4">
          <img
            alt={profile?.firstName}
            src={profile?.profilePicture || "/default-profile-img.png"}
            className="w-24 h-24 rounded-lg object-cover"
          />
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              <span className="text-lg font-medium uppercase text-gray-900 flex items-center gap-2 whitespace-nowrap">
                {profile?.firstName} {profile?.lastName}
              </span>
            </h2>
            <p className="text-gray-600 text-sm">{profile?.primaryPosition}</p>
            <p className="text-gray-600 text-sm">
              #{profile?.positionRank} (Overall Position Rank)
            </p>
            <p
              className={`text-gray-600 text-sm ${!isExpanded ? "hidden md:block" : ""}`}
            >
              {profile?.highSchool}, {getStateAbbreviation(profile?.state)} (
              {profile?.expectedYearOfGraduation})
            </p>
            <p
              className={`text-sm pt-1 whitespace-nowrap ${!isExpanded ? "hidden md:block" : ""}`}
            >
              <span className="font-normal text-gray-600 capitalize">
                Weight:{" "}
                <span className="font-semibold text-gray-800">
                  {profile?.weight}
                </span>
              </span>
              <span className="font-normal text-gray-600 capitalize pl-4">
                Height:
                <span className="font-semibold text-gray-800">
                  {profile?.heightFt}' {profile?.heightIn}"
                </span>
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className={`col-span-1 ${!isExpanded ? "hidden md:block" : ""}`}>
        {profile?.offerTier && (
          <div className="flex items-center justify-end">
            <div className="mt-2 rounded-xl bg-gray-100 p-4 shadow-sm">
              <div className="text-base font-bold text-gray-900">
                {profile?.offerTier}
              </div>
              <div className="mt-0.5 text-sm text-gray-600">Offer Tier</div>
            </div>
          </div>
        )}
      </div>

      <div className={`col-span-1 ${!isExpanded ? "hidden md:block" : ""}`}>
        {(profile?.offerAcceptanceStatus || profile?.offerAcceptanceSchool) && (
          <div className="flex items-center justify-end">
            <div className="mt-2 rounded-xl bg-gray-100 p-4 shadow-sm">
              <div className="text-base font-bold text-blue-600">
                {profile?.offerAcceptanceStatus}
              </div>
              {profile?.offerAcceptanceSchool && (
                <div className="text-base font-bold text-red-600">
                  {profile?.offerAcceptanceSchool}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={`col-span-1 ${!isExpanded ? "hidden md:block" : ""}`} />

      <div
        className={`col-span-1 pt-0 md:pt-0 lg:pt-2 flex justify-center ${!isExpanded ? "hidden md:flex" : ""}`}
      >
        <div className="flex flex-col items-center">
          <CircularProgressBar
            progress={profile?.positionScore || 0}
            size={80}
            strokeWidth={5}
          />
          <span className="text-[9px] text-gray-900 -mt-8">Position Score</span>
        </div>
      </div>

      <div className="col-span-3 md:hidden flex justify-center pt-2 pb-1">
        <button
          type="button"
          className="text-blue-600 text-sm flex items-center gap-1 hover:opacity-70"
          onClick={() => setIsExpanded((v) => !v)}
        >
          {isExpanded ? "View less ∧" : "View more ∨"}
        </button>
      </div>
    </div>
  );
}

function ScoreInfoModal({ title, description, onClose }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg p-6 w-3/4 max-w-md">
        <h2 className="text-lg font-bold mb-4">{title}</h2>
        <p className="text-sm mb-6">{description}</p>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Close
        </button>
      </div>
    </div>
  );
}
